import { CS, CS_m } from 'back-end-api'
import { useTranslation } from 'services/Translation'
import { getClaims } from 'services/Authentication'
import { primary, purple, error } from 'styles/Colors'

export const ownRegistration = (registration: CS_m.ModelsRegistration): boolean => {
  return (registration.doctor_account_id || 0) !== 0
}

export const isLockedByCurrentUser = (registration: CS_m.ModelsRegistration): boolean => {
  const claims = getClaims()
  const currentUserId = claims?.sub ? parseInt(claims.sub, 10) : 0
  return registration.doctor_account_id === currentUserId
}

export const isLockedByOtherUser = (registration: CS_m.ModelsRegistration): boolean => {
  if (!registration.doctor_account_id || registration.doctor_account_id === 0) {
    return false
  }
  const claims = getClaims()
  const currentUserId = claims?.sub ? parseInt(claims.sub, 10) : 0
  return registration.doctor_account_id !== currentUserId
}

export const useStatusTranslation = () => {
  const statusTexts = (getText: (key: string, category: string) => string) => (key: string) =>
    ({
      Registered: getText('Registered', 'Registration_Status'),
      SummarySubmitted: getText('Summary Submitted', 'Registration_Status'),
      SummaryAdviceSent: getText('Advice Sent', 'Registration_Status'),
      SummaryReferralSent: getText('Feedback Sent', 'Registration_Status'),
      SummaryReferralSpecialSent: getText('Referral Sent', 'Registration_Status'),
      SummaryInProgress: getText('Summary In Progress', 'Registration_Status'),
      SummaryConsulted: getText('Summary Consulted', 'Registration_Status'),
      SummaryCancelled: getText('Summary Cancelled', 'Registration_Status'),
      SummaryTimeout: getText('Summary Timeout', 'Registration_Status'),
      AppointmentPending: getText('Appointment Pending', 'Registration_Status'),
      AppointmentAccepted: getText('Appointment Accepted', 'Registration_Status'),
      AppointmentReady: getText('Appointment Ready', 'Registration_Status'),
      AppointmentInProgress: getText('Appointment In Progress', 'Registration_Status'),
      AppointmentDone: getText('Appointment Done', 'Registration_Status'),
      AppointmentDeclined: getText('Appointment Declined', 'Registration_Status'),
      AppointmentCancelled: getText('Appointment Cancelled', 'Registration_Status'),
      AppointmentTimeout: getText('Appointment Timeout', 'Registration_Status'),
      AppointmentMissed: getText('Appointment Missed', 'Registration_Status'),
      DeclaredIll: getText('Declared ill', 'Patient_List_Table'),
      DeclaredNotIll: getText('Declared not ill', 'Patient_List_Table'),
    }[key])

  const { t } = useTranslation(statusTexts)
  return t
}

// ── Local status resolution ──
// We override CS.getStatus here because the bundled back-end-api may be cached
// by webpack and not reflect our fixes (appointment priority over summary status,
// active-appointment selection when multiple exist after reschedule).

const FINAL_APPOINTMENT_STATUSES: string[] = [
  CS_m.ModelsAppointmentStatus.done,
  CS_m.ModelsAppointmentStatus.declined,
  CS_m.ModelsAppointmentStatus.cancelled,
  CS_m.ModelsAppointmentStatus.timeout,
  CS_m.ModelsAppointmentStatus.missed,
]

/**
 * Select the most relevant appointment from a summary.
 *
 * Prefers an active (non-final) appointment. When all appointments are final
 * (e.g. after a reschedule where the original was declined and the new one
 * was missed), picks the most recently created one so the latest status is
 * displayed instead of an older one.
 *
 * Single source of truth for frontend appointment selection. Used by both
 * the main overview (via getRegistrationStatus) and the patient page
 * (via usePatientRouteParams).
 */
export const getActiveAppointmentFromSummary = (
  summary: CS_m.ModelsSummary | null | undefined
): CS_m.ModelsAppointment | null => {
  if (!summary?.appointments || summary.appointments.length === 0) return null
  if (summary.appointments.length === 1) {
    const appt = summary.appointments[0]
    appt.summary = summary
    return appt
  }

  const active = summary.appointments.find(
    (a) => !FINAL_APPOINTMENT_STATUSES.includes(a.status as string)
  )
  if (active) {
    active.summary = summary
    return active
  }

  // All appointments are final — pick the most recently created one
  // so the latest status (e.g. missed) is shown instead of an older one (e.g. declined).
  const sorted = [...summary.appointments].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  )
  const appt = sorted[0]
  appt.summary = summary
  return appt
}

/**
 * Compute the "last activity" timestamp for an encounter (registration).
 *
 * This mirrors the `statusTime`/`lastUpdate` value shown in the sidebar search
 * results (see services/Summaries/formatter.js): the most recent `updated_at`
 * between the encounter's summary and its most relevant appointment. This lets
 * the past-encounter history and the search results display the same
 * "most recent activity" date instead of the registration creation date.
 *
 * Falls back to the registration's `updated_at` (then `created_at`) when no
 * summary is available.
 */
export const getEncounterLastActivity = (
  registration: CS_m.ModelsRegistration | null | undefined
): Date | null => {
  if (!registration) return null

  const summary = CS.getSummary(registration)
  if (!summary) {
    const fallback = registration.updated_at ?? registration.created_at
    return fallback ? new Date(fallback) : null
  }

  const appointment = getActiveAppointmentFromSummary(summary)
  const summaryTime = summary.updated_at ? new Date(summary.updated_at).getTime() : 0
  const appointmentTime = appointment?.updated_at
    ? new Date(appointment.updated_at).getTime()
    : 0

  const lastActivity = Math.max(summaryTime, appointmentTime)
  if (lastActivity > 0) return new Date(lastActivity)

  return registration.created_at ? new Date(registration.created_at) : null
}

const mapAppointmentStatus = (
  status: string
): CS.RegistrationStatus | null => {
  switch (status) {
    case CS_m.ModelsAppointmentStatus.pending: return CS.RegistrationStatus.AppointmentPending
    case CS_m.ModelsAppointmentStatus.accepted: return CS.RegistrationStatus.AppointmentAccepted
    case CS_m.ModelsAppointmentStatus.ready: return CS.RegistrationStatus.AppointmentReady
    case CS_m.ModelsAppointmentStatus.inProgress: return CS.RegistrationStatus.AppointmentInProgress
    case CS_m.ModelsAppointmentStatus.done: return CS.RegistrationStatus.AppointmentDone
    case CS_m.ModelsAppointmentStatus.declined: return CS.RegistrationStatus.AppointmentDeclined
    case CS_m.ModelsAppointmentStatus.cancelled: return CS.RegistrationStatus.AppointmentCancelled
    case CS_m.ModelsAppointmentStatus.timeout: return CS.RegistrationStatus.AppointmentTimeout
    case CS_m.ModelsAppointmentStatus.missed: return CS.RegistrationStatus.AppointmentMissed
    default: return null
  }
}

/**
 * Compute the effective registration status.
 * Appointment status takes priority when an active appointment exists.
 */
export const getRegistrationStatus = (
  registration: CS_m.ModelsRegistration
): CS.RegistrationStatus => {
  const summary = CS.getSummary(registration)
  const appointment = summary ? getActiveAppointmentFromSummary(summary) : null

  if (summary) {
    // Appointment status takes priority when one exists
    if (appointment) {
      const apptStatus = mapAppointmentStatus(appointment.status as string)
      if (apptStatus !== null) return apptStatus
    }

    switch (summary.status) {
      case CS_m.ModelsSummaryStatus.inProgress: return CS.RegistrationStatus.SummaryInProgress
      case CS_m.ModelsSummaryStatus.submitted: return CS.RegistrationStatus.SummarySubmitted
      case CS_m.ModelsSummaryStatus.adviceSent: return CS.RegistrationStatus.SummaryAdviceSent
      case CS_m.ModelsSummaryStatus.referralSent: return CS.RegistrationStatus.SummaryReferralSent
      case CS_m.ModelsSummaryStatus.referralSpecialSent: return CS.RegistrationStatus.SummaryReferralSpecialSent
      case CS_m.ModelsSummaryStatus.consulted: return CS.RegistrationStatus.SummaryConsulted
      case CS_m.ModelsSummaryStatus.cancelled: return CS.RegistrationStatus.SummaryCancelled
      case CS_m.ModelsSummaryStatus.timeout: return CS.RegistrationStatus.SummaryTimeout
    }
  }

  return CS.RegistrationStatus.Registered
}

export const getStatusText = (
  registration: CS_m.ModelsRegistration,
  t: (key: string) => string
): string => {
  const status = getRegistrationStatus(registration)

  switch (status) {
    case CS.RegistrationStatus.Registered: return t('Registered')
    case CS.RegistrationStatus.SummarySubmitted: return t('SummarySubmitted')
    case CS.RegistrationStatus.SummaryAdviceSent: return t('SummaryAdviceSent')
    case CS.RegistrationStatus.SummaryReferralSent: return t('SummaryReferralSent')
    case CS.RegistrationStatus.SummaryReferralSpecialSent: return t('SummaryReferralSpecialSent')
    case CS.RegistrationStatus.SummaryInProgress: return t('SummaryInProgress')
    case CS.RegistrationStatus.SummaryConsulted: return t('SummaryConsulted')
    case CS.RegistrationStatus.SummaryCancelled: return t('SummaryCancelled')
    case CS.RegistrationStatus.SummaryTimeout: return t('SummaryTimeout')
    case CS.RegistrationStatus.AppointmentPending: return t('AppointmentPending')
    case CS.RegistrationStatus.AppointmentAccepted: return t('AppointmentAccepted')
    case CS.RegistrationStatus.AppointmentReady: return t('AppointmentReady')
    case CS.RegistrationStatus.AppointmentInProgress: return t('AppointmentInProgress')
    case CS.RegistrationStatus.AppointmentDone: return t('AppointmentDone')
    case CS.RegistrationStatus.AppointmentDeclined: return t('AppointmentDeclined')
    case CS.RegistrationStatus.AppointmentCancelled: return t('AppointmentCancelled')
    case CS.RegistrationStatus.AppointmentTimeout: return t('AppointmentTimeout')
    case CS.RegistrationStatus.AppointmentMissed: return t('AppointmentMissed')
    default: return '-'
  }
}

export const getStatusColor = (registration: CS_m.ModelsRegistration): string => {
  const status = getRegistrationStatus(registration)

  switch (status) {
    case CS.RegistrationStatus.Registered: return primary
    case CS.RegistrationStatus.SummarySubmitted: return '#E6A817'
    case CS.RegistrationStatus.SummaryAdviceSent: return '#7C3AED'
    case CS.RegistrationStatus.SummaryReferralSent: return '#7C3AED'
    case CS.RegistrationStatus.SummaryReferralSpecialSent: return '#7C3AED'
    case CS.RegistrationStatus.SummaryInProgress: return '#3B82F6'
    case CS.RegistrationStatus.SummaryConsulted: return '#15803D'
    case CS.RegistrationStatus.SummaryCancelled: return '#9CA3AF'
    case CS.RegistrationStatus.SummaryTimeout: return '#F97316'
    case CS.RegistrationStatus.AppointmentPending: return '#8B5CF6'
    case CS.RegistrationStatus.AppointmentAccepted: return purple
    case CS.RegistrationStatus.AppointmentReady: return '#06B6D4'
    case CS.RegistrationStatus.AppointmentInProgress: return '#2563EB'
    case CS.RegistrationStatus.AppointmentDone: return '#10B981'
    case CS.RegistrationStatus.AppointmentDeclined: return error
    case CS.RegistrationStatus.AppointmentCancelled: return '#EF4444'
    case CS.RegistrationStatus.AppointmentTimeout: return '#DC2626'
    case CS.RegistrationStatus.AppointmentMissed: return '#B91C1C'
    default: return primary
  }
}

// ── Summary-only status (ignores appointment status) ──
// Used in the main overview where the summary action (advice/referral) is the primary info.

const getSummaryOnlyStatus = (
  registration: CS_m.ModelsRegistration
): CS.RegistrationStatus => {
  const summary = CS.getSummary(registration)
  if (summary) {
    switch (summary.status) {
      case CS_m.ModelsSummaryStatus.inProgress: return CS.RegistrationStatus.SummaryInProgress
      case CS_m.ModelsSummaryStatus.submitted: return CS.RegistrationStatus.SummarySubmitted
      case CS_m.ModelsSummaryStatus.adviceSent: return CS.RegistrationStatus.SummaryAdviceSent
      case CS_m.ModelsSummaryStatus.referralSent: return CS.RegistrationStatus.SummaryReferralSent
      case CS_m.ModelsSummaryStatus.referralSpecialSent: return CS.RegistrationStatus.SummaryReferralSpecialSent
      case CS_m.ModelsSummaryStatus.consulted: return CS.RegistrationStatus.SummaryConsulted
      case CS_m.ModelsSummaryStatus.cancelled: return CS.RegistrationStatus.SummaryCancelled
      case CS_m.ModelsSummaryStatus.timeout: return CS.RegistrationStatus.SummaryTimeout
    }
  }
  return CS.RegistrationStatus.Registered
}

export const getSummaryStatusText = (
  registration: CS_m.ModelsRegistration,
  t: (key: string) => string
): string => {
  const status = getSummaryOnlyStatus(registration)
  switch (status) {
    case CS.RegistrationStatus.Registered: return t('Registered')
    case CS.RegistrationStatus.SummarySubmitted: return t('SummarySubmitted')
    case CS.RegistrationStatus.SummaryAdviceSent: return t('SummaryAdviceSent')
    case CS.RegistrationStatus.SummaryReferralSent: return t('SummaryReferralSent')
    case CS.RegistrationStatus.SummaryReferralSpecialSent: return t('SummaryReferralSpecialSent')
    case CS.RegistrationStatus.SummaryInProgress: return t('SummaryInProgress')
    case CS.RegistrationStatus.SummaryConsulted: return t('SummaryConsulted')
    case CS.RegistrationStatus.SummaryCancelled: return t('SummaryCancelled')
    case CS.RegistrationStatus.SummaryTimeout: return t('SummaryTimeout')
    default: return '-'
  }
}

export const getSummaryStatusColor = (
  registration: CS_m.ModelsRegistration
): string => {
  const status = getSummaryOnlyStatus(registration)
  switch (status) {
    case CS.RegistrationStatus.Registered: return primary
    case CS.RegistrationStatus.SummarySubmitted: return '#E6A817'
    case CS.RegistrationStatus.SummaryAdviceSent: return '#7C3AED'
    case CS.RegistrationStatus.SummaryReferralSent: return '#7C3AED'
    case CS.RegistrationStatus.SummaryReferralSpecialSent: return '#7C3AED'
    case CS.RegistrationStatus.SummaryInProgress: return '#3B82F6'
    case CS.RegistrationStatus.SummaryConsulted: return '#15803D'
    case CS.RegistrationStatus.SummaryCancelled: return '#9CA3AF'
    case CS.RegistrationStatus.SummaryTimeout: return '#F97316'
    default: return primary
  }
}
